import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Video, CheckCircle, ArrowLeft, Clock, MapPin, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { formatDateTime, formatTime } from '@/lib/dateFormat';

// ────────────────────────────────────────────────────────────────────────────
// Public "Book a Demo" page (no auth) — a clone of the tenant public BookingPage
// design: page header + left info panel + right month calendar → time slots →
// details form. Creates a Zoom demo + a Prospect on the super-admin side.
// ────────────────────────────────────────────────────────────────────────────

const _env = import.meta.env as any;
const API_BASE = _env.VITE_API_URL ? `${_env.VITE_API_URL}/api` : '/api';
const pub = axios.create({ baseURL: API_BASE });

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
function getAllTimezones(): string[] {
  const anyIntl = Intl as any;
  if (typeof anyIntl.supportedValuesOf === 'function') {
    try { return anyIntl.supportedValuesOf('timeZone'); } catch { /* noop */ }
  }
  return [BROWSER_TZ, 'UTC', 'Asia/Kolkata', 'Asia/Calcutta', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Asia/Dubai', 'Asia/Singapore'];
}
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = (n: number) => String(n).padStart(2, '0');
const toDateStr = (y: number, mo: number, d: number) => `${y}-${pad(mo + 1)}-${pad(d)}`;

interface DemoConfig {
  active: boolean;
  eventName?: string;
  duration?: number;
  timezone?: string;
  host?: string;
  futureLimit?: number;
  workingDays?: number[];
}

export function BookDemoPage() {
  const { adminId: paramAdminId } = useParams();
  const [adminId, setAdminId] = useState(paramAdminId || '');
  const [cfg, setCfg] = useState<DemoConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState<string>('');
  const [guestTz, setGuestTz] = useState<string>(BROWSER_TZ);
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [form, setForm] = useState({ guestName: '', guestEmail: '', company: '', notes: '' });
  const [countries, setCountries] = useState<{ id: string; name: string; flagEmoji?: string | null; dialCode?: string | null }[]>([]);
  const [countryName, setCountryName] = useState('');
  const [isd, setIsd] = useState('');
  const [mobile, setMobile] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    pub.get('/geo/countries').then(({ data }) => setCountries(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let id = paramAdminId || '';
      if (!id) {
        try { const { data } = await pub.get('/public/demo'); if (data?.adminId) id = data.adminId; } catch { /* unavailable */ }
      }
      if (!id) { if (!cancelled) { setCfg({ active: false }); setLoading(false); } return; }
      if (!cancelled) setAdminId(id);
      try {
        const { data } = await pub.get(`/public/demo/${id}`);
        if (!cancelled) setCfg(data);
      } catch {
        if (!cancelled) setCfg({ active: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [paramAdminId]);

  useEffect(() => {
    if (!date || !cfg?.active) return;
    setSlotsLoading(true);
    setPicked(null);
    pub.get(`/public/demo/${adminId}/slots`, { params: { date } })
      .then(({ data }) => setSlots(Array.isArray(data) ? data : []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [date, adminId, cfg]);

  const countryNames = countries.map(c => c.name);
  const isdOptions = countries.filter(c => c.dialCode).map(c => ({ label: `${c.flagEmoji ? `${c.flagEmoji} ` : ''}${c.dialCode} ${c.name}`, dial: c.dialCode || '' }));

  const submit = async () => {
    if (!picked) return;
    if (!form.guestName.trim() || !form.guestEmail.trim()) { setError('Please enter your name and email.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const dial = isdOptions.find(o => o.label === isd)?.dial || '';
      const guestPhone = mobile.trim() ? `${dial} ${mobile.trim()}`.trim() : undefined;
      const countryId = countries.find(c => c.name === countryName)?.id;
      const { data } = await pub.post(`/public/demo/${adminId}`, {
        start: picked,
        ...form,
        guestPhone,
        countryId: countryId || undefined,
        timezone: guestTz,
        browser: { tz: BROWSER_TZ, lang: navigator.language, screen: `${window.screen?.width}x${window.screen?.height}` },
      });
      setDone(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not book the demo. Please try another slot.');
    } finally {
      setSubmitting(false);
    }
  };

  const fmtTime = (iso: string) => formatTime(iso, guestTz);
  const fmtDateTime = (iso: string) => formatDateTime(iso, guestTz);
  const host = 'Your Advisor';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/40">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/70 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center text-white font-bold text-sm shadow-md">A</div>
          <span className="font-bold text-slate-900 text-base tracking-tight">{cfg?.eventName || 'Book a Demo'}</span>
        </div>
        <a href="https://brixlead.com" target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-[#6366F1] transition-colors">
          Powered by <span className="font-bold bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] bg-clip-text text-transparent">BrixLead</span>
        </a>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {loading || !cfg ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" /></div>
        ) : !cfg.active ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-10 text-center max-w-md mx-auto">
            <Clock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <h1 className="text-lg font-bold text-slate-800">Demo booking unavailable</h1>
            <p className="text-sm text-slate-500 mt-1">This demo link isn't accepting bookings right now.</p>
          </div>
        ) : done ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-10 text-center max-w-md mx-auto">
            <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><CheckCircle className="h-7 w-7 text-emerald-600" /></div>
            <h1 className="text-xl font-bold text-slate-900">You're booked! 🎉</h1>
            <p className="text-sm text-slate-600 mt-2">{done.eventName} with {host}</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{fmtDateTime(done.startAt)}</p>
            <p className="text-xs text-slate-400">({guestTz})</p>
            {done.meetingLink ? (
              <a href={done.meetingLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-5 px-5 h-11 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold">
                <Video className="h-4 w-4" /> Join Zoom meeting
              </a>
            ) : (
              <p className="text-xs text-slate-500 mt-5">We'll email you the meeting link shortly.</p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col lg:flex-row">
            {/* Left info panel */}
            <div className="lg:w-80 flex-shrink-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 border-b lg:border-b-0 lg:border-r border-slate-100 p-7 space-y-5">
              <div className="flex flex-col items-start gap-3">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center text-white font-bold text-xl shadow-md ring-4 ring-white">A</div>
                <div>
                  <p className="font-bold text-slate-900 text-lg leading-tight">{host}</p>
                  <p className="text-sm text-slate-500 mt-0.5">BrixLead</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">Book a time that works for you.</p>
              <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200/60 to-transparent" />
              <div className="space-y-2.5">
                <p className="font-semibold text-slate-900 text-sm">{cfg.eventName}</p>
                <div className="space-y-2 text-sm text-slate-600">
                  <span className="flex items-center gap-2.5"><Chip><Clock className="h-3.5 w-3.5 text-slate-500" /></Chip>{cfg.duration} min</span>
                  <span className="flex items-center gap-2.5"><Chip><MapPin className="h-3.5 w-3.5 text-slate-500" /></Chip>Zoom</span>
                  <span className="flex items-center gap-2.5">
                    <Chip><Globe className="h-3.5 w-3.5 text-slate-500" /></Chip>
                    <span className="flex-1 min-w-0">
                      <SearchableSelect
                        options={getAllTimezones()}
                        value={guestTz}
                        onChange={(v) => setGuestTz(v || BROWSER_TZ)}
                        placeholder="Select your timezone"
                      />
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Right content */}
            <div className="flex-1 min-w-0 p-7">
              {!picked ? (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="h-10 w-10 rounded-xl bg-indigo-50 flex items-center justify-center"><Clock className="h-5 w-5 text-[#6366F1]" /></span>
                    <div>
                      <p className="font-bold text-slate-900">{cfg.eventName}</p>
                      <p className="text-xs text-slate-500">{cfg.duration} min · {host}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Select a date</p>
                      <CalPicker cfg={cfg} selected={date} onSelect={setDate} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                        {date ? 'Available times' : 'Pick a date first'} {date && <span className="normal-case font-normal text-slate-400">({guestTz})</span>}
                      </p>
                      {!date ? (
                        <p className="text-sm text-slate-400 py-8 text-center">Choose a date to see open times.</p>
                      ) : slotsLoading ? (
                        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
                      ) : slots.length === 0 ? (
                        <p className="text-sm text-slate-400 py-8 text-center">No open slots — try another date.</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 max-h-[360px] overflow-y-auto pr-1">
                          {slots.map(s => (
                            <button key={s} onClick={() => setPicked(s)} className="px-2 h-11 rounded-lg border border-slate-200 hover:border-[#6366F1] hover:bg-indigo-50 text-sm font-medium text-slate-700 transition-colors">
                              {fmtTime(s)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="max-w-md">
                  <button onClick={() => setPicked(null)} className="inline-flex items-center gap-1 text-sm text-[#6366F1] hover:underline mb-4"><ArrowLeft className="h-3.5 w-3.5" /> Back to times</button>
                  <div className="bg-indigo-50/60 rounded-lg p-3 text-sm text-slate-700 font-medium mb-4">
                    {fmtDateTime(picked)} <span className="text-slate-400 font-normal">({guestTz})</span>
                  </div>
                  <div className="space-y-3">
                    <input placeholder="Your name *" value={form.guestName} onChange={e => setForm({ ...form, guestName: e.target.value })} className={INPUT} />
                    <input type="email" placeholder="Email *" value={form.guestEmail} onChange={e => setForm({ ...form, guestEmail: e.target.value })} className={INPUT} />
                    <div className="flex gap-2">
                      <div className="w-40 flex-shrink-0">
                        <SearchableSelect options={isdOptions.map(o => o.label)} value={isd} onChange={setIsd} placeholder="ISD code" />
                      </div>
                      <input placeholder="Mobile number" value={mobile} onChange={e => setMobile(e.target.value)} className={INPUT + ' flex-1'} />
                    </div>
                    <input placeholder="Company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className={INPUT} />
                    {countries.length > 0 && (
                      <SearchableSelect options={countryNames} value={countryName} onChange={setCountryName} placeholder="Country (optional)" />
                    )}
                    <textarea placeholder="Anything you'd like us to cover?" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={INPUT + ' resize-none'} />
                  </div>
                  {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
                  <button onClick={submit} disabled={submitting} className="mt-4 w-full h-11 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Confirm demo
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="h-7 w-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm flex-shrink-0">{children}</span>;
}

// Month calendar — mirrors the tenant BookingPage CalPicker; greys past,
// beyond futureLimit, and non-working days.
function CalPicker({ cfg, selected, onSelect }: { cfg: DemoConfig; selected: string; onSelect: (d: string) => void }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const first = new Date(year, month, 1).getDay();
  const dim = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);

  const workingDays = cfg.workingDays ?? [1, 2, 3, 4, 5];
  const limit = cfg.futureLimit ?? 0;
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  const maxDate = limit > 0 ? new Date(midnight.getTime() + limit * 86400000) : null; // 0 = unlimited

  const disabled = (d: number) => {
    const dt = new Date(year, month, d);
    if (dt < midnight) return true;
    if (maxDate && dt > maxDate) return true;
    return !workingDays.includes(dt.getDay());
  };

  const atMin = year === today.getFullYear() && month === today.getMonth();
  const prev = () => { if (atMin) return; month === 0 ? (setMonth(11), setYear(y => y - 1)) : setMonth(m => m - 1); };
  const next = () => month === 11 ? (setMonth(0), setYear(y => y + 1)) : setMonth(m => m + 1);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={prev} disabled={atMin} className="h-9 w-9 flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-30 transition-colors shadow-sm">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="font-bold text-slate-900 text-base tracking-tight">{MONTHS[month]} {year}</span>
        <button onClick={next} className="h-9 w-9 flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] transition-colors shadow-sm">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map(d => <div key={d} className="text-center text-[11px] font-semibold text-slate-500 py-1.5 uppercase tracking-wider">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = toDateStr(year, month, d);
          const off = disabled(d);
          const sel = selected === ds;
          return (
            <button key={i} onClick={() => !off && onSelect(ds)} disabled={off}
              className={`h-10 w-10 mx-auto rounded-full text-sm font-medium transition-all ${off ? 'text-slate-300 cursor-not-allowed' : sel ? 'bg-[#6366F1] text-white shadow-md' : 'text-slate-800 hover:bg-indigo-50 hover:text-[#6366F1]'}`}>
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const INPUT = 'w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]';
